import { CheckCircle, ArrowRight, Calendar, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { pricingPlans } from '../mockData';

const MonthlyMarketing = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-yellow-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Monthly Marketing Packages
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Digital marketing on a monthly basis, at a fraction of the cost.
          </p>
        </div>
      </section>

      {/* Quote */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#FBD116]/10 border-l-4 border-[#FBD116] p-8 rounded-r-2xl">
            <p className="text-2xl text-gray-900 font-medium italic">
              "We Unite as one of your staff, at a fraction of the cost, with strategic capability."
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            All programs can cover content marketing such as blog writing, social media posts, and email marketing. Digital marketing, website development, website management, SEO, and strategy can be added.
          </p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative border-2 transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular
                    ? 'border-[#FBD116] shadow-xl scale-105'
                    : 'border-gray-200 hover:border-[#FBD116]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-[#FBD116] text-gray-900 px-6 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-sm text-gray-600 mb-4">{plan.hours} Hours/Month</div>
                    <div className="text-4xl font-bold text-gray-900">{plan.price}</div>
                  </div>
                  <p className="text-gray-600 mb-6 text-center">{plan.description}</p>
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-[#FBD116] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    asChild
                    className={`w-full ${
                      plan.popular
                        ? 'bg-[#FBD116] hover:bg-[#E5C015] text-gray-900'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    } font-semibold`}
                  >
                    <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                      Get Started
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Bespoke Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Bespoke Projects
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            Project-based work comes out of opportunity. Often we find a vertical or market that needs extra communication or sales effort. Project-based work is a great way to move on opportunities quickly.
          </p>
          <p className="text-gray-600">
            It's important to note that each of these monthly marketing packages will be tailored to your organization. Hopefully, this allows you some perspective on how quickly we might be able to deliver the foundations of an integrated marketing campaign every month, without fail.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Monthly Marketing?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Calendar,
                title: 'Consistency',
                description: 'Regular, predictable marketing output that keeps your brand top-of-mind.'
              },
              {
                icon: Target,
                title: 'Strategic',
                description: 'Every piece of content is aligned with your business goals and target audience.'
              },
              {
                icon: TrendingUp,
                title: 'Scalable',
                description: 'Start small and scale up as you see results and your business grows.'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#FBD116] transition-all">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#FBD116]/10 flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need Help Choosing?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a free marketing audit to find out what suits your business needs.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
          >
            <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
              Book Free Marketing Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default MonthlyMarketing;
